import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import XBadge from "@/components/XBadge";
import { DialogDescription } from "@radix-ui/react-dialog";
import {} from "cmdk";
import { CirclePlus } from "lucide-react";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import {
  useSearchTags,
  useServicesTags,
} from "../../routes/overview/hooks/tags";
import { TagBadge } from "../ui/diagram/collapsible-tag-list";

const AddTagsDialog = ({
  trigger: TriggerComponent = (
    <Button
      variant="borderless"
      size="sm"
      onClick={(ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        setOpen(true);
      }}
    >
      <CirclePlus className="size-4" />
      Add tags
    </Button>
  ),
  service,
  tags: initialTags = [],
  open = false,
  setOpen = () => {},
  withTrigger = true,
}) => {
  const [tags, setTags] = useState([]);
  const { createTag } = useServicesTags();
  const [currentTag, setCurrentTag] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const { searchTags } = useSearchTags();
  const [showSuggestions, setShowSuggestions] = useState(false);

  const handleAddTag = (tagName) => {
    if (tagName.trim() !== "" && !tags.includes(tagName)) {
      setTags(tags.concat(tagName));
      setCurrentTag("");
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove.replace("#", "")));
  };

  const handleInputChange = async (event) => {
    const value = event.target.value;
    setCurrentTag(value);

    if (value.trim()) {
      const results = await searchTags(value);
      setSuggestions(results);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && currentTag.trim()) {
      event.preventDefault();
      handleAddTag(currentTag);
    } else if (event.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const handleSave = () => {
    createTag(service, tags);
    setOpen(false);
  };

  useEffect(() => {
    setTags(initialTags);
    return () => {
      setTags([]);
    };
  }, [initialTags]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {withTrigger && <DialogTrigger asChild>{TriggerComponent}</DialogTrigger>}
      <DialogContent>
        <DialogDescription />
        <DialogHeader>
          <DialogTitle>Edit Tags</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <Label htmlFor="tags">Current tags</Label>
          <div className="grid grid-cols-4 items-center gap-4">
            <div className="col-span-3 flex flex-wrap gap-2">
              {tags?.length === 0
                ? "-"
                : tags.map((tag) => (
                    <XBadge
                      key={tag}
                      label={`#${tag}`}
                      handleRemove={handleRemoveTag}
                    />
                  ))}
            </div>
          </div>
          <Label htmlFor="new-tag">Tag</Label>
          <div className="grid grid-cols-4 items-center gap-4">
            <div className="col-span-3 relative">
              <Input
                id="new-tag"
                value={currentTag}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="# Type..."
                className="col-span-3"
              />
              {showSuggestions && (
                <div className="absolute w-full mt-1 border rounded-md shadow-lg  z-50 overflow-hidden">
                  <Command>
                    <CommandList>
                      <CommandEmpty>No suggestions found</CommandEmpty>
                      {suggestions.length > 0 && (
                        <CommandGroup>
                          {suggestions.map((suggestion) => (
                            <CommandItem
                              key={suggestion.name}
                              onSelect={() => handleAddTag(suggestion.name)}
                              className="flex justify-between cursor-pointer"
                            >
                              <TagBadge tag={suggestion.name} />
                              <span className="text-sm text-gray-500">
                                Used {suggestion.count} time
                                {suggestion.count != 1 ? "s" : ""}
                              </span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </div>
              )}
            </div>
            <Button
              variant="borderless"
              className="font-semibold"
              onClick={() => handleAddTag(currentTag)}
            >
              <CirclePlus className="size-8" />
              Add Tag
            </Button>
          </div>
        </div>
        <div className="flex justify-between">
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Back
          </Button>
          <Button type="submit" onClick={handleSave}>
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

AddTagsDialog.propTypes = {
  trigger: PropTypes.element.isRequired,
  service: PropTypes.string.isRequired,
  tags: PropTypes.array,
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  withTrigger: PropTypes.bool,
};

export default AddTagsDialog;
